package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.ui.unit.dp
import io.github.koalaplot.core.xygraph.FloatLinearAxisModel
import io.github.koalaplot.core.xygraph.rememberFloatLinearAxisModel
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.log10
import kotlin.math.pow

internal fun niceStep(
    max: Float,
    divisions: Int = 5,
): Float {
    val raw = (max / divisions).coerceAtLeast(Float.MIN_VALUE)
    val magnitude = 10f.pow(floor(log10(raw)))
    val normalized = raw / magnitude
    val nice =
        when {
            normalized <= 1f -> 1f
            normalized <= 2f -> 2f
            normalized <= 5f -> 5f
            else -> 10f
        }
    return nice * magnitude
}

@Composable
internal fun rememberValueAxis(
    max: Float,
    divisions: Int = 5,
    headroom: Float = 1f,
): FloatLinearAxisModel {
    val safeMax = max.coerceAtLeast(1f)
    val step = niceStep(safeMax, divisions)
    val axisMax = (ceil(safeMax * headroom / step) * step).coerceAtLeast(step)
    return rememberFloatLinearAxisModel(
        range = 0f..axisMax,
        minimumMajorTickIncrement = step,
        minimumMajorTickSpacing = 1.dp,
        minorTickCount = 0,
    )
}

@Composable
internal fun rememberPercentAxis(): FloatLinearAxisModel =
    rememberFloatLinearAxisModel(
        range = 0f..100f,
        minimumMajorTickIncrement = 25f,
        minimumMajorTickSpacing = 1.dp,
        minorTickCount = 0,
    )

@Composable
internal fun rememberSignedValueAxis(
    lo: Float,
    hi: Float,
    divisions: Int = 5,
): FloatLinearAxisModel {
    val high = hi.coerceAtLeast(0f)
    val low = lo.coerceAtMost(0f)
    val step = niceStep((high - low).coerceAtLeast(1f), divisions)
    val axisMax = (ceil(high / step) * step).coerceAtLeast(step)
    val axisMin = (floor(low / step) * step).coerceAtMost(0f)
    return rememberFloatLinearAxisModel(
        range = axisMin..axisMax,
        minimumMajorTickIncrement = step,
        minimumMajorTickSpacing = 1.dp,
        minorTickCount = 0,
    )
}
